import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Send,
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageSquare,
  ArrowRight,
} from "lucide-react";

const FILTER_API_URL = "/api/filter";
const CONTACT_API_URL = "/api/contact";



const API_VALUES = {
  inquiryType: "E99D4F7955A205E4EAE3420211F1",
  budgetRange: "D31C738F248AC9836BAF420111F1",
  timeline: "492C283964EA39461AA5420211F1",
};



const contactInfo = [
  {
    icon: Mail,
    label: "Email",
    value: "INFO@SQUARETRIGGER.COM",
    href: "mailto:INFO@SQUARETRIGGER.COM",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "236-869-335",
    href: "tel:236869335",
  },
  {
    icon: MapPin,
    label: "Location",
    value: "Vancouver, BC",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Mon – Fri: 9:00 AM – 6:00 PM PT",
  },
];

const initialFormData = {
  fullName: "",
  companyName: "",
  email: "",
  phone: "",
  service: "",
  projectDetails: "",
};

export default function ContactPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
const [isLoadingServices, setIsLoadingServices] = useState(false);

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  const phoneRegex = useMemo(
    () =>
      /^(?:\+1\s?)?(?:\([2-9]\d{2}\)|[2-9]\d{2})[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/,
    []
  );
  const fetchServiceOptions = async () => {
  setIsLoadingServices(true);
  setApiError("");

  try {
    const response = await fetch(FILTER_API_URL, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    const result = await response.json();

    if (!response.ok || !result?.success) {
      throw new Error(result?.message || "Unable to load services.");
    }

    setServiceOptions(result?.data?.SERVICE_NEEDED || []);
  } catch (error) {
    setServiceOptions([]);
    setApiError(
      error?.message || "Unable to load services. Please refresh the page."
    );
  } finally {
    setIsLoadingServices(false);
  }
};

useEffect(() => {
  fetchServiceOptions();
}, []);

 const handleChange = (e) => {
  const { name, value } = e.target;

  let finalValue = value;

  // Full Name: allow alphabets and spaces only
  if (name === "fullName") {
    finalValue = value.replace(/[^A-Za-z\s]/g, "");
  }

  setFormData((prev) => ({
    ...prev,
    [name]: finalValue,
  }));

  setErrors((prev) => ({
    ...prev,
    [name]: "",
  }));

  setApiError("");
};

  const handleServiceChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      service: value,
    }));

    setErrors((prev) => ({
      ...prev,
      service: "",
    }));

    setApiError("");
  };

  const scrollToForm = () => {
    const formSection = document.getElementById("contact-form");

    if (formSection) {
      formSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

   if (!formData.fullName.trim()) {
  newErrors.fullName = "Full name is required.";
} else if (!/^[A-Za-z\s]+$/.test(formData.fullName.trim())) {
  newErrors.fullName = "Full name can contain alphabets only.";
}

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!phoneRegex.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number.";
    }

    if (!formData.service.trim()) {
      newErrors.service = "Please select a service.";
    }

    if (!formData.projectDetails.trim()) {
      newErrors.projectDetails = "Project details are required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const buildMessage = () => {
  return formData.projectDetails.trim();
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError("");

    try {
      const payload = {
        name: formData.fullName.trim(),
       inquiryType: formData.service,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company_name: formData.companyName.trim(),
        message: buildMessage(),
        current_website:
          typeof window !== "undefined"
            ? window.location.href
            : "https://squaretrigger.com/contact",
        budget_range: API_VALUES.budgetRange,
        timeline: API_VALUES.timeline,
      };

      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let responseData = null;

      try {
        responseData = await response.json();
      } catch {
        responseData = null;
      }

      if (!response.ok) {
        const message =
          responseData?.message ||
          responseData?.error ||
          "Something went wrong. Please try again.";

        throw new Error(message);
      }

      setFormData(initialFormData);
      setErrors({});
      setApiError("");

      navigate("/");
    } catch (error) {
      setApiError(
        error?.message || "Unable to send your message. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Us to know Us Better | Square Trigger"
        description="Get the best digital services. Contact Square Trigger now to transform your digital presence. Get in touch now."
        keywords={SEO_KEYWORDS.contact}
      />

      {/* Hero Section */}
<section className="relative overflow-hidden">
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: "url('/contact.webp')" }}
  />

  
  <div className="absolute inset-0 bg-gradient-to-br from-foreground/88 via-foreground/72 to-foreground/45" />

  <div className="absolute inset-0">
          <div className="absolute top-16 right-6 h-72 w-72 rounded-full bg-primary/20 blur-3xl sm:top-20 sm:right-20 sm:h-72 sm:w-96" />
          <div className="absolute bottom-16 left-6 w-56 rounded-full bg-secondary/20 blur-3xl sm:bottom-20 sm:left-20 sm:w-56" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 sm:mb-2 flex justify-center lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <MessageSquare className="h-4 w-4" />
                  <span className="text-sm font-medium">Contact Us</span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='m-0 mb-[18px] max-w-[760px] mx-auto font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Let&apos;s Talk About What Your{" "}
                <span className="gradient-text">Business Needs</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="m-0 mb-8 max-w-[560px] mx-auto text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                Whether you need a better website, stronger visibility, or a
                custom digital solution, we&apos;re here to help.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex justify-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  onClick={scrollToForm}
                  className="group inline-flex min-h-[52px] w-full sm:w-auto items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold"
                >
                  <span className="flex items-center justify-center gap-2">
                    Send Us a Message
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Button>
              </motion.div>
            </div>

          
<div className="hidden lg:flex items-center justify-center min-h-[420px]">
  <div className="h-[420px] w-full max-w-[520px]" />
</div>
          </div>
        </div>
      </section>

      {/* Contact Form + Details */}
      <section
        id="contact-form"
        className="pt-12 sm:pt-14 lg:pt-16 pb-8 sm:pb-10 lg:pb-12 bg-background relative overflow-hidden scroll-mt-24"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-10 xl:gap-12 items-start">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3 order-2 lg:order-1 h-full"
            >
              <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-soft h-full">
                <div className="mb-6 sm:mb-8 text-center">
                  <span className="text-sm font-medium text-primary uppercase tracking-wider">
                    Contact Form
                  </span>

                  <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mt-3 mb-3">
                    Tell Us About Your Project
                  </h2>

                  <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                    Share a few details and we&apos;ll get back to you with the
                    right next step.
                  </p>
                </div>

                {apiError && (
                  <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
                    {apiError}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name</Label>

                     <Input
  id="fullName"
  name="fullName"
  value={formData.fullName}
  onChange={handleChange}
  placeholder="Your full name"
  required
  pattern="[A-Za-z\s]+"
  title="Please enter alphabets only"
  className="h-12"
/>

                      {errors.fullName && (
                        <p className="text-sm text-destructive">
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="companyName">Company Name</Label>

                      <Input
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>

                      <Input
                        id="email"
                        name="email"
                        type="email"
                        inputMode="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required
                        className="h-12"
                      />

                      {errors.email && (
                        <p className="text-sm text-destructive">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>

                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="555-123-4567"
                        required
                        className="h-12"
                      />

                      {errors.phone && (
                        <p className="text-sm text-destructive">
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="service">Service Interested In</Label>

                      <Select
  value={formData.service}
  onValueChange={handleServiceChange}
  disabled={isLoadingServices}
>
  <SelectTrigger className="h-12">
    <SelectValue
      placeholder={
        isLoadingServices ? "Loading services..." : "Select a service"
      }
    />
  </SelectTrigger>

  <SelectContent>
    {serviceOptions.map((service) => (
      <SelectItem key={service.value} value={service.value}>
        {service.label}
      </SelectItem>
    ))}
  </SelectContent>
</Select>

                      {errors.service && (
                        <p className="text-sm text-destructive">
                          {errors.service}
                        </p>
                      )}
                    </div>

                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="projectDetails">Project Details</Label>

                      <Textarea
                        id="projectDetails"
                        name="projectDetails"
                        value={formData.projectDetails}
                        onChange={handleChange}
                        placeholder="Tell us what you need, what is not working right now, or what you want to improve."
                        rows={6}
                        required
                        className="resize-none min-h-[150px]"
                      />

                      {errors.projectDetails && (
                        <p className="text-sm text-destructive">
                          {errors.projectDetails}
                        </p>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="w-full mt-8 group"
                    disabled={isSubmitting || isLoadingServices}
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2 order-1 lg:order-2 h-full"
            >
              <div className="h-full flex flex-col">
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-primary uppercase tracking-wider block">
                    Contact Details
                  </span>

                  <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mt-3">
                    Get in Touch
                  </h2>
                </div>

                <div className="space-y-6">
                  {contactInfo.map((item, index) => {
                    const Icon = item.icon;

                    const content = (
                      <div className="flex items-start gap-4 min-h-[72px]">
                        <div className="p-3 rounded-xl bg-accent shrink-0">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>

                        <div className="min-w-0 pt-0.5">
                          <h3 className="text-base font-semibold text-foreground">
                            {item.label}
                          </h3>

                          <p className="text-sm text-muted-foreground break-words mt-1">
                            {item.value}
                          </p>
                        </div>
                      </div>
                    );

                    return (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.5,
                          delay: index * 0.08,
                        }}
                        className="bg-card rounded-2xl border border-border p-5 shadow-soft"
                      >
                        {item.href ? (
                          <a
                            href={item.href}
                            className="block hover:opacity-80 transition-opacity"
                          >
                            {content}
                          </a>
                        ) : (
                          content
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                <div className="bg-card rounded-2xl border border-border p-5 shadow-soft mt-6">
                  <div className="min-h-[72px] flex flex-col justify-center">
                    <p className="text-sm text-muted-foreground mb-1">Note</p>

                    <p className="font-medium text-foreground">
                      Remote-first agency. Meetings by appointment only.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}